import countriesData from '@data/countries';
import siteStats from '@data/site-stats';
import { ChevronRight, Search } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import PageHero from '@/components/page-hero';
import SEO from '@/components/seo';
import { Muted, Section, StatsRow } from '@/components/ui';
import config from '@/config';
import i18n from '@/i18n.json';
import { loadMessages } from '@/lib/messages';
import { up } from '@/themes';
import { flagForCountryCode } from '@/utils';
import { shareImage } from '@/utils/share-image';

/* Sort and group by the name without its article ("The Bahamas" under B). */
const sortName = (name) => name.replace(/^The /, '');

/* Case- and accent-insensitive matching for the search box. */
const normalise = (value) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

const sortedCountries = [...countriesData].sort((a, b) =>
  sortName(a.name).localeCompare(sortName(b.name), i18n.defaultLocale)
);

const Stats = styled(StatsRow)`
  margin: 24px auto 0;
  max-width: 960px;
`;

const SearchBox = styled.label`
  align-items: center;
  background-color: var(--card);
  border-radius: 9999px;
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-sm);
  color: var(--muted-foreground);
  display: flex;
  gap: 12px;
  height: 48px;
  max-width: 480px;
  padding: 0 20px;
  transition: box-shadow 150ms;

  &:focus-within {
    box-shadow:
      0 0 0 1px var(--ring),
      0 0 0 4px color-mix(in oklab, var(--ring) 25%, transparent);
  }

  svg {
    flex-shrink: 0;
  }
`;

const SearchInput = styled.input.attrs({ type: 'search' })`
  appearance: none;
  background: none;
  border: none;
  color: var(--foreground);
  flex: 1;
  font-family: inherit;
  font-size: 16px;
  height: 100%;
  min-width: 0;
  outline: none;
  padding: 0;

  &::placeholder {
    color: var(--muted-foreground);
    opacity: 1;
  }

  &:focus-visible {
    outline: none;
  }
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 32px;
`;

const Group = styled.section`
  display: grid;
  gap: 12px;

  ${up.md} {
    gap: 24px;
    grid-template-columns: 48px minmax(0, 1fr);
  }
`;

const Letter = styled.h2`
  color: var(--primary);
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 32px;
  margin: 0;

  ${up.md} {
    padding-top: 10px;
  }
`;

const Grid = styled.ul`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  list-style: none;
  margin: 0;
  padding: 0;

  @media (min-width: 420px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${up.lg} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${up.xl} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const CountryCard = styled(Link)`
  align-items: center;
  background-color: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  color: var(--card-foreground);
  display: flex;
  gap: 12px;
  min-height: 56px;
  padding: 12px 12px 12px 16px;
  text-decoration: none;
  transition:
    border-color 200ms,
    box-shadow 200ms,
    transform 200ms;

  svg {
    color: var(--muted-foreground);
    flex-shrink: 0;
    transition:
      color 200ms,
      transform 200ms;
  }

  @media (hover: hover) {
    &:hover {
      border-color: color-mix(in oklab, var(--primary) 50%, transparent);
      box-shadow: var(--shadow-md);
      transform: translateY(-2px);

      svg {
        color: var(--primary);
        transform: translateX(2px);
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:hover svg {
      transform: none;
    }
  }
`;

const FlagIcon = styled.span`
  flex-shrink: 0;
  font-size: 24px;
  line-height: 1;
`;

const CountryName = styled.span`
  flex: 1;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  min-width: 0;
  overflow-wrap: break-word;
`;

const Empty = styled(Muted)`
  font-size: 16px;
  line-height: 24px;
  margin-top: 32px;
`;

const Countries = () => {
  const t = useTranslations('countries');
  const { locale } = useRouter();
  const [query, setQuery] = useState('');
  const needle = normalise(query);

  // [[letter, countries]], alphabetical, filtered by the search box.
  const groups = useMemo(() => {
    const byLetter = new Map();

    sortedCountries.forEach((country) => {
      if (needle && !normalise(country.name).includes(needle)) return;

      const letter = sortName(country.name).charAt(0).toUpperCase();
      if (!byLetter.has(letter)) byLetter.set(letter, []);
      byLetter.get(letter).push(country);
    });

    return [...byLetter.entries()];
  }, [needle]);

  const canonicalUrl = `${config.rootUrl}${locale === i18n.defaultLocale ? '' : `/${locale}`}/countries`;

  const stats = [
    { value: siteStats.labels(locale).brandCount, label: t('statsBrands') },
    { value: sortedCountries.length.toLocaleString(locale), label: t('statsCountries') },
    { value: t('statsDeliveryValue'), label: t('statsDelivery') },
  ];

  return (
    <>
      <SEO
        title={t('seoTitle')}
        description={t('seoDescription')}
        url={canonicalUrl}
        hreflangPath="/countries"
        imageUrl={shareImage.countries(locale)}
        imageAlt={t('title')}
      />

      <PageHero
        align="center"
        eyebrow={t('eyebrow')}
        title={t('title')}
        description={t('subTitle')}
      />

      <Section id="stats" $spacing="none">
        <Stats stats={stats} />
      </Section>

      <Section id="countries" $spacing="compact">
        <SearchBox>
          <Search aria-hidden="true" size={20} strokeWidth={1.75} />
          <SearchInput
            aria-label={t('searchLabel')}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('searchPlaceholder')}
            value={query}
          />
        </SearchBox>

        {groups.length === 0 && <Empty role="status">{t('noResults')}</Empty>}

        <Groups>
          {groups.map(([letter, countries]) => (
            <Group key={letter} aria-labelledby={`countries-${letter}`}>
              <Letter id={`countries-${letter}`}>{letter}</Letter>
              <Grid>
                {countries.map((country) => (
                  <li key={country.id}>
                    <CountryCard href={`/buy/${country.slug}`} prefetch={false}>
                      <FlagIcon aria-hidden="true">{flagForCountryCode(country.id)}</FlagIcon>
                      <CountryName>{country.name}</CountryName>
                      <ChevronRight aria-hidden="true" size={16} strokeWidth={1.75} />
                    </CountryCard>
                  </li>
                ))}
              </Grid>
            </Group>
          ))}
        </Groups>
      </Section>
    </>
  );
};

export async function getStaticProps({ locale }) {
  return {
    props: {
      messages: loadMessages(locale),
    },
  };
}

export default Countries;
